import { useTranslation } from 'react-i18next';
import cn from 'classnames';
import type { InterpretiveReportDto } from '../../../hooks/queries/useGetInterpretiveReportQuery';
import {
  agreementFromApi,
  confidenceTone,
  decimal,
  responseRate,
  scaleMax,
} from '../interpretiveReportView';
import { agreementTones } from '../../../components/AnalysisLayout/analysis.utils';
import { ToneChip } from './Tone';
import styles from '../InterpretiveReportPage.module.css';

type ReportCoverProps = {
  report: InterpretiveReportDto;
  locale: string;
  date: string;
};

/** The first page: what the report is, the team's archetype, the overall score and the data behind it */
export const ReportCover = ({ report, locale, date }: ReportCoverProps) => {
  const { t } = useTranslation();
  const { summary, methodology } = report;
  const agreement = agreementFromApi(summary.agreementLevel);

  return (
    <>
      <div className={cn(styles.cover, styles.keep)}>
        <div aria-hidden="true" className={styles.coverCircle} />
        <div className={styles.coverContent}>
          <span className={styles.coverEyebrow}>{t('interpretiveReport.document.cover.eyebrow')}</span>
          <h1 className={styles.coverTitle}>{t('interpretiveReport.document.cover.title')}</h1>
          <span className={styles.dateChip}>{date}</span>
        </div>
      </div>

      <div className={cn(styles.archetype, styles.keep)}>
        <div className={styles.archetypeText}>
          <span className={styles.label}>{t('interpretiveReport.document.archetypeLabel')}</span>
          <h2 className={styles.archetypeName}>{report.archetype.name}</h2>
          <p className={styles.body}>{report.archetype.description}</p>
        </div>
        <div className={styles.overall}>
          <span className={styles.label}>{t('interpretiveReport.document.overall.label')}</span>
          <span className={styles.overallScore}>
            {decimal(summary.overallScore, locale)}
            <span className={styles.outOf}>/{scaleMax(locale)}</span>
          </span>
          <ToneChip tone={agreementTones[agreement]}>{t(`teamResult.agreement.${agreement}`)}</ToneChip>
          <span className={styles.meta}>
            {t('interpretiveReport.document.overall.spread', {
              sd: decimal(summary.averageStandardDeviation, locale),
            })}
          </span>
        </div>
      </div>

      <div className={cn(styles.dataBasis, styles.keep)}>
        <span className={styles.dataBasisTitle}>{t('interpretiveReport.document.dataBasis.title')}</span>
        <span className={styles.dataBasisFacts}>
          <span>
            <span className={styles.factLabel}>{t('interpretiveReport.document.dataBasis.respondents')}</span>{' '}
            {t('interpretiveReport.document.dataBasis.respondentsValue', {
              count: methodology.respondents,
              expected: methodology.expectedRespondents,
              rate: responseRate(methodology.responseRate, locale),
            })}
          </span>
          <span>
            <span className={styles.factLabel}>{t('interpretiveReport.document.dataBasis.measurement')}</span>{' '}
            {t('interpretiveReport.document.dataBasis.measurementValue', {
              number: methodology.measurementNumber,
              total: methodology.totalMeasurements,
            })}
          </span>
          <span className={styles.fact}>
            <span className={styles.factLabel}>{t('interpretiveReport.document.dataBasis.quality')}</span>
            <ToneChip tone={confidenceTone[methodology.confidenceLevel]}>
              {t(`interpretiveReport.document.dataBasis.confidence.${methodology.confidenceLevel}`)}
            </ToneChip>
          </span>
        </span>
      </div>
    </>
  );
};
